import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, SideNav, type SideNavItem, type SideNavSection } from '@bravemove/ui-web';
import {
  ADMIN_NAV_COUNT_PERMISSIONS,
  type AdminNavCountKey,
  type Permission,
} from '@bravemove/types';
import { OPERATIONS_NAV, PLATFORM_NAV, TOOLS_NAV, type NavItem } from './navItems';
import { useNavCounts } from '../navCounts/useNavCount';
import { usePlatformFeatures } from '../hooks/usePlatformFeatures';

/** FIX 5.27 — WhatsApp tooling hides while `features.whatsapp` is off. */
const WHATSAPP_NAV_IDS: ReadonlySet<string> = new Set(['whatsapp-templates', 'whatsapp-quality']);

/** F60b — the badge each nav item shows (the venue sub-nav's are wired below). */
const BADGE_KEY_BY_NAV_ID: Readonly<Record<string, AdminNavCountKey>> = {
  verifications: 'verifications',
  'venue-claims': 'venueClaims',
  'venue-photos': 'venuePhotos',
  'payment-disputes': 'paymentDisputes',
  'commission-statements': 'commissionStatements',
  'whatsapp-templates': 'whatsappTemplates',
  'skill-disputes': 'skillDisputes',
  'open-game-reports': 'openGameReports',
  'trainer-verifications': 'trainerVerifications',
  'review-moderation': 'flaggedReviews',
  'placement-approvals': 'placementApprovals',
  promotions: 'suspendedPromotions',
  tournaments: 'suspendedTournaments',
  reports: 'openReports',
  'gdpr-requests': 'pendingGdpr',
  finance: 'financeReconciliation',
};

export interface AdminSideNavProps {
  readonly permissions: readonly Permission[];
}

/**
 * F06/F09/F10 — left-rail navigation for the admin shell. Reads the actor's
 * resolved permission list and hides nav items the actor cannot reach.
 *
 * Pending-count badges (F09 onwards) come from `NavCountsProvider` (F60b): one
 * `GET /admin/nav-counts` per visible tab. A badge shows only when the actor
 * holds the permission that gates its queue (`ADMIN_NAV_COUNT_PERMISSIONS`)
 * and its count is above zero.
 */
export function AdminSideNav({ permissions }: AdminSideNavProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // FIX 5.27 (§13.11-13) — WhatsApp delivery is deferred: hide its tooling
  // until `GET /auth/config` reports `features.whatsapp` (as portal + mobile do).
  const { whatsapp: whatsappEnabled } = usePlatformFeatures();
  const { counts } = useNavCounts();
  // A badge counts only when the actor holds its queue's permission (the API
  // already omits the others; this keeps the gate explicit on the client too).
  const badgeCount = (key: AdminNavCountKey): number =>
    permissions.includes(ADMIN_NAV_COUNT_PERMISSIONS[key]) &&
    (key !== 'whatsappTemplates' || whatsappEnabled)
      ? (counts[key] ?? 0)
      : 0;
  const countForNavId = (id: string): number => {
    const key = BADGE_KEY_BY_NAV_ID[id];
    return key ? badgeCount(key) : 0;
  };

  // F10 — the venue catalogue sub-nav badges.
  const canModerateVenues = permissions.includes('venues:moderate');
  const flaggedVenuesCount = badgeCount('flaggedVenues');
  const submissionsCount = badgeCount('venueSubmissions');
  const editsCount = badgeCount('venueEdits');

  const itemToSideNavItem = (item: NavItem): SideNavItem => {
    const count = countForNavId(item.id);
    // The placement monitor (`/placements`) must NOT highlight when on the
    // analytics sub-route (`/placements/analytics`) — that item owns its own
    // highlight. Every other item uses the generic prefix-active rule.
    const active = item.id === 'placements' ? path === '/placements' : isActive(path, item.route);
    return {
      label: t(item.labelKey),
      icon: item.iconName,
      active,
      href: item.route,
      ...(count > 0
        ? {
            badge: (
              <Badge tone="warning" size="sm">
                {count}
              </Badge>
            ),
          }
        : {}),
    };
  };

  // F10 — venue-specific nav items injected after the main "Venues" item
  const venueItems: SideNavItem[] = permissions.includes('venues:read')
    ? [
        {
          label: t('admin:venue.nav.allVenues'),
          icon: 'pin' as const,
          active:
            path === '/venues' ||
            (path.startsWith('/venues/') &&
              !path.startsWith('/venues/submissions') &&
              !path.startsWith('/venues/edits') &&
              !path.startsWith('/venues/new')),
          href: '/venues',
          ...(flaggedVenuesCount > 0
            ? {
                badge: (
                  <Badge tone="warning" size="sm">
                    {flaggedVenuesCount}
                  </Badge>
                ),
              }
            : {}),
        },
        ...(canModerateVenues
          ? [
              {
                label: t('admin:venue.nav.submissions'),
                icon: 'receipt' as const,
                active: isActive(path, '/venues/submissions'),
                href: '/venues/submissions',
                ...(submissionsCount > 0
                  ? {
                      badge: (
                        <Badge tone="warning" size="sm">
                          {submissionsCount}
                        </Badge>
                      ),
                    }
                  : {}),
              },
              {
                label: t('admin:venue.nav.edits'),
                icon: 'edit' as const,
                active: isActive(path, '/venues/edits'),
                href: '/venues/edits',
                ...(editsCount > 0
                  ? {
                      badge: (
                        <Badge tone="warning" size="sm">
                          {editsCount}
                        </Badge>
                      ),
                    }
                  : {}),
              },
            ]
          : []),
      ]
    : [];

  // ─── Top "Modules" block (flat) + collapsible groups below ─────────────────
  // The most-used surfaces (Dashboard, the venue sub-nav, Users) stay FLAT at
  // the top — the original always-visible "Modules" section. Everything else is
  // folded into collapsible level-1 groups so the long tail no longer needs
  // scrolling. A collapsed group shows an aggregate pending-count badge; the
  // group holding the active route auto-opens.
  const navById = new Map<string, NavItem>(
    [...OPERATIONS_NAV, ...PLATFORM_NAV, ...TOOLS_NAV].map((it) => [it.id, it]),
  );

  const navItemFor = (id: string): SideNavItem | null => {
    const navItem = navById.get(id);
    if (!navItem) return null;
    if (navItem.permission && !permissions.includes(navItem.permission)) return null;
    if (!whatsappEnabled && WHATSAPP_NAV_IDS.has(id)) return null;
    return itemToSideNavItem(navItem);
  };

  const buildGroupItems = (ids: readonly string[]): SideNavItem[] => {
    const out: SideNavItem[] = [];
    for (const id of ids) {
      const item = navItemFor(id);
      if (item) out.push(item);
    }
    return out;
  };

  // Level-1 groups, ordered supply → demand → moderation → money → tooling.
  // (The "Venues" group is built separately below because it folds in the
  // hand-built venue catalogue sub-nav + its badges.)
  const GROUPS: ReadonlyArray<{
    readonly title: string;
    readonly icon: NavItem['iconName'];
    readonly ids: readonly string[];
  }> = [
    {
      title: t('admin:nav.group.trainers', { defaultValue: 'Trainers & Clubs' }),
      icon: 'team',
      ids: [
        'trainer-verifications',
        'trainer-package-consumptions',
        'clubs',
        'skill-disputes',
        'sport-skill-descriptors',
      ],
    },
    {
      title: t('admin:nav.group.players', { defaultValue: 'Players & Teams' }),
      icon: 'team',
      ids: ['teams', 'open-games', 'open-game-reports'],
    },
    {
      title: t('admin:nav.group.moderation', { defaultValue: 'Moderation' }),
      icon: 'shield',
      ids: [
        'verifications',
        'review-moderation',
        'badge-catalog',
        'review-tags',
        'reports',
        'gdpr-requests',
      ],
    },
    {
      title: t('admin:nav.group.finance', { defaultValue: 'Finance' }),
      icon: 'wallet',
      ids: ['finance', 'payment-disputes', 'payments', 'commission-rates', 'commission-statements'],
    },
    {
      title: t('admin:nav.group.monetization', { defaultValue: 'Monetization' }),
      icon: 'receipt',
      ids: [
        'subscription-tiers',
        'venue-subscriptions',
        'placement-inventory',
        'placement-approvals',
        'placements',
        'promotions',
        'tournaments',
        'tournament-organizers',
      ],
    },
    {
      title: t('admin:nav.group.analytics', { defaultValue: 'Analytics' }),
      icon: 'cup',
      ids: ['analytics', 'placement-analytics', 'promotion-analytics', 'tournament-analytics'],
    },
    {
      title: t('admin:nav.group.tools', { defaultValue: 'Tools' }),
      icon: 'eye',
      ids: [
        'notifications-dry-run',
        'notifications-test-send',
        'email-suppressions',
        'whatsapp-templates',
        'whatsapp-quality',
      ],
    },
    {
      title: t('admin:nav.group.system', { defaultValue: 'System' }),
      icon: 'settings',
      ids: ['financial', 'cancellation-policy', 'admin-users', 'audit-log', 'ops-logs', 'account'],
    },
  ];

  const sections: SideNavSection[] = [];

  // Flat "Overview" — the two universal, always-visible daily surfaces.
  const overviewItems: SideNavItem[] = [];
  const dashboardItem = navItemFor('dashboard');
  if (dashboardItem) overviewItems.push(dashboardItem);
  const usersItem = navItemFor('users');
  if (usersItem) overviewItems.push(usersItem);
  sections.push({
    title: t('admin:nav.modules', { defaultValue: 'Overview' }),
    items: overviewItems,
  });

  // "Venues" group — the venue catalogue sub-nav (All venues / Submissions /
  // Edits, with their badges) consolidated with Claims / B2B accounts / Photos,
  // so every venue + business surface lives in one logical place.
  const venuesGroupItems: SideNavItem[] = [
    ...venueItems,
    ...buildGroupItems(['venue-claims', 'b2b-accounts', 'venue-photos', 'bookings']),
  ];
  if (venuesGroupItems.length > 0) {
    const venuesCount =
      flaggedVenuesCount +
      submissionsCount +
      editsCount +
      countForNavId('venue-claims') +
      countForNavId('venue-photos');
    sections.push({
      title: t('admin:nav.group.venues', { defaultValue: 'Venues' }),
      icon: 'pin',
      collapsible: true,
      // Open by default so every link is visible at a glance (the user can still
      // collapse a group). Collapsed-by-default hid items like GDPR.
      defaultOpen: true,
      items: venuesGroupItems,
      ...(venuesCount > 0
        ? {
            badge: (
              <Badge tone="warning" size="sm">
                {venuesCount}
              </Badge>
            ),
          }
        : {}),
    });
  }

  for (const group of GROUPS) {
    const items = buildGroupItems(group.ids);
    if (items.length === 0) continue;
    const groupCount = group.ids.reduce((sum, id) => sum + countForNavId(id), 0);
    sections.push({
      title: group.title,
      icon: group.icon,
      collapsible: true,
      // Open by default so all links are visible (collapsible if the user wants).
      defaultOpen: true,
      items,
      ...(groupCount > 0
        ? {
            badge: (
              <Badge tone="warning" size="sm">
                {groupCount}
              </Badge>
            ),
          }
        : {}),
    });
  }

  // `onNavigate` gives every href-based item SPA navigation on plain clicks
  // while preserving anchor semantics (cmd/middle-click → open in new tab).
  return <SideNav sections={sections} onNavigate={(href) => navigate(href)} />;
}

function isActive(path: string, route: string): boolean {
  if (route === '/') return path === '/';
  return path === route || path.startsWith(`${route}/`);
}
